import React, { useState, useEffect } from 'react';
import './SettingsPage.css';

const SettingsPage = ({ onCancel }) => {
  const [priceTable, setPriceTable] = useState({
    spot: {
      '0': 0,
      '6': 20,
      '10': 5,
      '16': 30,
      '20': 15,
    },
    realtime: {
      '0': 10,
      '6': 20,
      '10': 5,
      '16': 45,
      '20': 20,
    },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');

  const timeCategories = [
    { label: '深夜〜早朝', startHour: 0, endHour: 6, key: '0' },
    { label: '朝ピーク', startHour: 6, endHour: 10, key: '6' },
    { label: '日中', startHour: 10, endHour: 16, key: '10' },
    { label: '夕方ピーク', startHour: 16, endHour: 20, key: '16' },
    { label: '夜間', startHour: 20, endHour: 24, key: '20' },
  ];

  useEffect(() => {
    fetchCurrentSettings();
  }, []);

  const fetchCurrentSettings = async () => {
    setLoading(true);
    setError(null);
    setMessage('');
    try {
      const response = await fetch('/v1/settings/prices');
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setPriceTable(data);
      setMessage('現在の設定を読み込みました。');
    } catch (e) {
      setError(e);
      setMessage(`設定の読み込みに失敗しました: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (market, hourKey, value) => {
    setPriceTable((prevPrices) => ({
      ...prevPrices,
      [market]: {
        ...prevPrices[market],
        [hourKey]: Number(value),
      },
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setMessage('');
    try {
      const response = await fetch('/v1/settings/prices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceTable }),
      });
      if (!response.ok) throw new Error(`設定の保存に失敗しました。`);
      const data = await response.json();
      setMessage(data.message || '設定が保存されました。');
    } catch (e) {
      setError(e);
      setMessage(`設定の保存に失敗しました。`);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('初期設定に戻します。よろしいですか？')) {
      return;
    }
    setLoading(true);
    setError(null);
    setMessage('');
    try {
      const response = await fetch('/v1/settings/prices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'reset' }), // 初期化アクションを送信
      });
      if (!response.ok) throw new Error(`設定の初期化に失敗しました。`);
      const data = await response.json();
      setMessage(data.message || '設定が初期化されました。');
      fetchCurrentSettings(); // 初期化後、最新の設定を再読み込み
    } catch (e) {
      setError(e);
      setMessage(`設定の初期化に失敗しました。`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="settings-loading">設定を読み込み中...</div>;
  if (error && !message) return <div className="settings-error">エラー: {error.message}</div>; // メッセージがある場合はそちらを表示

  return (
    <div className="settings-page">
      <h1 className="settings-title">設定</h1>
      
      <div className="settings-group">
        <h2 className="group-title">単価設定</h2>
        <p className="group-description">時間帯と取引方法ごとの電力単価を設定します。</p>
        
        {message && <div className={`settings-message ${error ? 'error' : ''}`}>{message}</div>}

        <div className="price-table-container">
          <table className="price-table">
            <thead>
              <tr>
                <th>時間区分</th>
                <th>スポット市場 (円/kWh)</th>
                <th>当日市場 (時間前) (円/kWh)</th>
              </tr>
            </thead>
            <tbody>
              {timeCategories.map((category) => (
                <tr key={category.key}>
                  <td>{category.label} ({category.startHour}:00 - {category.endHour}:00)</td>
                  <td>
                    <input
                      type="number"
                      value={priceTable.spot[category.key] || 0}
                      onChange={(e) => handlePriceChange('spot', category.key, e.target.value)}
                      className="price-input"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={priceTable.realtime[category.key] || 0}
                      onChange={(e) => handlePriceChange('realtime', category.key, e.target.value)}
                      className="price-input"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="settings-actions">
          <button className="control-btn save-btn" onClick={handleSave} disabled={loading}>保存</button>
          <button className="control-btn cancel-btn" onClick={onCancel} disabled={loading}>キャンセル</button>
          <button className="control-btn reset-btn" onClick={handleReset} disabled={loading}>初期設定に戻す</button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
